"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.conceptMapJobId = exports.conceptMapJobPayloadSchema = exports.CELL_OUTCOME = exports.CONCEPT_MAP_STATUS = exports.CONCEPT_MAP_QUEUE = void 0;
const zod_1 = require("zod");
/**
 * #715 — what a cell of an old study becomes in today's tree.
 *
 * A snapshot (#714) records the shape of the questionnaire a study was answered
 * against. That is enough to say two studies are comparable; it is not enough to
 * say WHERE a cell went when the tree moved under it. The rule's own uuid
 * survives a reparenting, but not a rewrite: the authorisations refactor of
 * 4 September 2026 replaced « Destinations des constructions autorisées » with
 * twelve questions, and every uuid in that branch is new.
 *
 * The durable key is the CONCEPT — what a question asks, independent of its
 * label and its place — plus the `dimensionValues` that say which scope of that
 * concept this particular question fixes. All twelve of those new questions
 * carry `allowed_destinations`; a reference held by concept would have crossed
 * the refactor without losing a cell.
 *
 * This module is the contract between the sweeper that notices work is due and
 * the worker that does it. Both live in `jobs`, but the shape is shared so the
 * backend can read a map without importing the pipeline.
 */
/** BullMQ queue carrying "build the concept map for this snapshot". */
exports.CONCEPT_MAP_QUEUE = 'urban_planning_concept_map';
exports.CONCEPT_MAP_STATUS = {
    /** The row exists and names its two inputs; the map has not been built yet. */
    PENDING: 'PENDING',
    /** Built against the snapshot and the annotations the row names. */
    COMPLETE: 'COMPLETE',
};
/**
 * Why a cell of the snapshot is, or is not, reachable in today's tree.
 *
 * The issue asks for these four by name, and for the count of each: the share
 * that no longer resolves is the **reference staleness rate**, and it is worth
 * as much as the mapping itself. A cell that quietly vanished is the failure
 * this feature exists to make impossible.
 */
exports.CELL_OUTCOME = {
    /** Exactly one living rule asks the same concept at the same scope. */
    LINKED: 'LINKED',
    /**
     * SEVERAL do. Not an error and not a loss: one question became many, which is
     * precisely what the authorisations refactor did. The cell resolves to all of
     * them and a reader decides — but it must never be silently collapsed to the
     * first.
     */
    SPLIT: 'SPLIT',
    /**
     * The concept has no living rule at all. A genuinely withdrawn question — six
     * of the thirteen v2 cells are these — and it is EXCLUDED explicitly, with
     * this reason recorded, rather than dropped.
     */
    RETIRED: 'RETIRED',
    /**
     * The snapshot's rule carries no annotation, so nothing durable was ever
     * recorded about what it asks. Distinct from RETIRED on purpose: this one is
     * fixable by annotating, and counting it separately is what says how much of
     * the staleness is our own bookkeeping.
     */
    NO_CONCEPT: 'NO_CONCEPT',
};
/**
 * The job payload — BOTH inputs, never just the snapshot.
 *
 * The map depends on two things that move independently: the tree, which lives
 * in the database, and the annotations, which live in the repository. Keying on
 * the snapshot alone would make a deploy that only changes annotations invisible
 * to the queue, which is the failure the issue calls out ("un job purement
 * quotidien manquerait la moitié des raisons de recalculer").
 */
exports.conceptMapJobPayloadSchema = zod_1.z.object({
    snapshotId: zod_1.z.string().uuid(),
    annotationsDigest: zod_1.z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * The revision of the LIVING tree the map must be built against — the third
     * input, and the one the issue does not name. The snapshot fixes the tree a
     * study ANSWERED; this fixes the tree the cells are resolved INTO, and it
     * moves whenever the catalogue is edited. Without it a map keeps offering a
     * rule that has since been retired, and nothing notices.
     */
    treeModifiedAt: zod_1.z.string().min(1),
});
/**
 * Deterministic BullMQ job id, over ALL THREE inputs.
 *
 * Carrying the digest is what makes an annotations change re-enqueue work that a
 * snapshot-only id would dedupe away as "already done"; carrying the tree
 * revision does the same for a catalogue edit.
 *
 * Joined with HYPHENS, and it must stay that way: BullMQ refuses a custom job id
 * containing `:` unless it splits into exactly three parts, and it throws inside
 * `Queue.add` before Redis is touched — the mistake #714 shipped and had to fix.
 * A uuid and a hex digest contain no colon, so this one cannot trip the rule.
 */
const conceptMapJobId = (snapshotId, annotationsDigest, treeModifiedAt) => 
// The revision is a timestamp, which carries `:` — and BullMQ counts colons.
// Stripped to digits so the whole id keeps none at all.
`concept-map-${snapshotId}-${annotationsDigest.slice(0, 12)}-${treeModifiedAt.replace(/\D/g, '')}`;
exports.conceptMapJobId = conceptMapJobId;
//# sourceMappingURL=conceptMap.js.map